import { useEffect } from "react";
import type { ThemeModeType } from "@shared/types/theme-mode.types";

import { useAppThemeContext } from "@frontend/shared/presentation/contexts/AppThemeProvider";

/**
 * Apply event-level theme setting via the AppThemeProvider context.
 * The `?theme=` query param is handled at the provider level as a session-wide
 * override — when active, all setThemeMode calls from this hook are ignored.
 *
 * @param themeMode - The theme mode from event settings:
 *   - 'light': Force light theme
 *   - 'dark': Force dark theme
 *   - 'system': Respect device/browser preference
 *   - undefined: Do not update context (theme persists from previous value)
 */
export function useEventTheme(themeMode: ThemeModeType | undefined): void {
  const { setThemeMode } = useAppThemeContext();

  useEffect(() => {
    if (themeMode !== undefined) {
      setThemeMode(themeMode);
    }
  }, [themeMode, setThemeMode]);
}
